import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MapPin, Package, Clock, TrendingDown } from "lucide-react";
import ReportDialog from "@/components/ReportDialog";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function BriefDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [brief, setBrief] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [availability, setAvailability] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/briefs/${id}`);
    setBrief(data);
    if (user) {
      try {
        const q = await api.get(`/briefs/${id}/quotes`);
        setQuotes(q.data.items || []);
      } catch { setQuotes([]); }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user?.user_id]);

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Log in to submit a quote"); return; }
    if (user.user_type !== "removal_company") { toast.error("Only removal companies can submit quotes"); return; }
    setSubmitting(true);
    try {
      await api.post(`/briefs/${id}/quotes`, {
        amount: parseFloat(amount),
        message,
        availability,
      });
      toast.success("Quote submitted");
      setAmount(""); setMessage(""); setAvailability("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  if (!brief) return <div className="mx-auto max-w-7xl px-6 py-16">Loading…</div>;

  const isOwner = user?.user_id === brief.poster_id;
  const canQuote = user?.user_type === "removal_company" && !isOwner && brief.status === "open";

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-10">
      <Link to="/briefs" className="overline text-muted-foreground hover:text-forest">← Clearance requests</Link>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-8">
        <div>
          <div className="border border-border bg-white p-8 lg:p-10">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="rounded-none bg-forest text-white overline">{brief.status}</Badge>
              <Badge variant="outline" className="rounded-none border-border overline">{brief.poster_type.replace("_", " ")}</Badge>
              {brief.is_featured && (
                <Badge className="rounded-none bg-safety text-white overline">⚡ Boosted</Badge>
              )}
            </div>
            <h1 className="mt-4 font-display font-black text-3xl lg:text-4xl tracking-tighter text-forest">{brief.title}</h1>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{brief.location}{brief.postcode ? ` · ${brief.postcode}` : ""}</span>
              <span className="flex items-center gap-1.5"><Package className="w-4 h-4" />{brief.approx_items}</span>
              {brief.move_by_date && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />Move by {brief.move_by_date}</span>}
            </div>
            <p className="mt-6 leading-relaxed whitespace-pre-line text-foreground/90">{brief.description}</p>

            <div className="mt-8 pt-6 border-t border-border grid grid-cols-2 sm:grid-cols-4 gap-6">
              <div>
                <div className="overline text-muted-foreground">Posted by</div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{brief.poster_name}</span>
                  <VerifiedBadge verified={brief.poster_is_verified} size="sm" testId="brief-poster-verified" />
                </div>
              </div>
              <Meta label="Budget" value={brief.budget != null ? `£${Number(brief.budget).toFixed(0)}` : "Open"} />
              <Meta label="Quotes in" value={brief.quote_count} />
              <Meta label="Posted" value={new Date(brief.created_at).toLocaleDateString("en-GB")} />
            </div>

            {isOwner && brief.status === "open" && !brief.is_featured && (
              <div className="mt-6 pt-4 border-t border-border flex items-center justify-between gap-3">
                <div>
                  <div className="overline text-safety">Get quoted 3-5x faster</div>
                  <div className="text-sm text-muted-foreground">Boost this request to the top of the list from £14.99.</div>
                </div>
                <Button asChild data-testid="brief-boost-cta" className="rounded-none bg-safety hover:bg-safety-hover text-white h-11 px-6">
                  <Link to={`/briefs/${brief.brief_id}/boost`}>⚡ Boost request</Link>
                </Button>
              </div>
            )}

            {!isOwner && user && (
              <div className="mt-6 pt-4 border-t border-border">
                <ReportDialog targetType="brief" targetId={brief.brief_id} testId="report-brief" label="Report this brief" />
              </div>
            )}
          </div>

          {/* Quotes list */}
          {user && (isOwner || quotes.length > 0) && (
            <div className="mt-6 border border-border bg-white p-8">
              <div className="overline text-safety">{isOwner ? "Quotes received" : "Your quote"}</div>
              <div className="mt-1 font-display text-2xl tracking-tight text-forest">
                {quotes.length} {isOwner ? `quote${quotes.length === 1 ? "" : "s"}` : ""}
              </div>
              <div className="mt-4 divide-y divide-border">
                {quotes.length === 0 && <div className="text-sm text-muted-foreground py-4">No quotes yet.</div>}
                {quotes.map((q, i) => (
                  <div key={q.quote_id} data-testid={`quote-item-${q.quote_id}`} className="py-4 grid grid-cols-1 md:grid-cols-[1fr,160px] gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        {i === 0 && isOwner && <TrendingDown className="w-4 h-4 text-safety" />}
                        <div className="font-display text-lg tracking-tight">{q.company_name}</div>
                      </div>
                      {q.availability && <div className="overline text-[10px] text-muted-foreground mt-1">Available {q.availability}</div>}
                      <p className="mt-2 text-sm leading-relaxed text-foreground/90 whitespace-pre-line">{q.message}</p>
                    </div>
                    <div className="text-right">
                      <div className="overline text-muted-foreground">Quote</div>
                      <div className="font-display text-3xl text-forest tabular-nums">£{Number(q.amount).toFixed(0)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 h-fit">
          {canQuote ? (
            <form onSubmit={submitQuote} className="border border-border bg-white p-6 lg:p-8 space-y-4">
              <div>
                <div className="overline text-safety">Submit a quote</div>
                <div className="mt-1 font-display text-xl tracking-tight text-forest">Bid on this brief</div>
              </div>
              <div>
                <div className="overline text-muted-foreground mb-2">Amount (£) *</div>
                <Input required type="number" min="0" step="50" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="quote-amount" className="rounded-none h-12" />
              </div>
              <div>
                <div className="overline text-muted-foreground mb-2">Availability</div>
                <Input value={availability} onChange={(e) => setAvailability(e.target.value)} placeholder="Any time in Feb, weekend only…" data-testid="quote-availability" className="rounded-none h-12" />
              </div>
              <div>
                <div className="overline text-muted-foreground mb-2">Message *</div>
                <Textarea required rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What's included, insurance, recycling, timeframe…" data-testid="quote-message" className="rounded-none" />
              </div>
              <Button type="submit" disabled={submitting} data-testid="quote-submit" className="w-full rounded-none bg-safety hover:bg-safety-hover text-white h-12">
                {submitting ? "Submitting…" : "Submit quote"}
              </Button>
            </form>
          ) : !user ? (
            <div className="border border-border bg-white p-6">
              <div className="overline text-safety">Removal firm?</div>
              <div className="mt-1 font-display text-xl tracking-tight text-forest">Log in to quote</div>
              <p className="mt-2 text-sm text-muted-foreground">Verified removal companies can bid on open briefs.</p>
              <Button asChild className="mt-4 w-full rounded-none bg-forest hover:bg-forest-800 text-white h-11">
                <Link to="/login">Log in</Link>
              </Button>
            </div>
          ) : isOwner ? (
            <div className="border border-border bg-white p-6">
              <div className="overline text-safety">Your brief</div>
              <div className="mt-1 font-display text-xl tracking-tight text-forest">Waiting on quotes</div>
              <p className="mt-2 text-sm text-muted-foreground">You'll see removal firm quotes below as they come in.</p>
            </div>
          ) : (
            <div className="border border-border bg-white p-6">
              <div className="overline text-muted-foreground">Only removal companies can quote on briefs.</div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="overline text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium">{value}</div>
    </div>
  );
}
